import { MAX_COMPETITION_PREDICTED_SCORE } from '../worldcup.constants';

/**
 * Parse a score string the same way the competition pick route does:
 * finite number → Math.trunc; otherwise invalid.
 */
export function parseCompetitionPickScore(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  const t = Math.trunc(n);
  if (t < 0 || t > MAX_COMPETITION_PREDICTED_SCORE) return null;
  return t;
}

/**
 * Resolve the score pair to persist. A blank side ("-") defaults to 0 when the
 * other side is a valid number, so a one-sided edit of a masked monkey pick
 * ("2 vs -") saves as "2 vs 0" instead of silently never persisting.
 *
 * Returns null when neither side is set (nothing to save yet) or when a
 * non-empty side is invalid (out of range / non-numeric) — never persist garbage.
 */
export function resolveScoresForPersist(
  home: string,
  away: string,
): { home: number; away: number } | null {
  const h = parseCompetitionPickScore(home);
  const a = parseCompetitionPickScore(away);
  if (h == null && a == null) return null;
  const resolvedHome = h != null ? h : home.trim() === '' ? 0 : null;
  const resolvedAway = a != null ? a : away.trim() === '' ? 0 : null;
  if (resolvedHome == null || resolvedAway == null) return null;
  return { home: resolvedHome, away: resolvedAway };
}

/**
 * Current numeric value for arrows / clamping; empty or invalid → 0 in UI.
 */
export function scoreStringToSteppableInt(raw: string): number {
  const p = parseCompetitionPickScore(raw);
  return p ?? 0;
}

/**
 * Apply delta and clamp to [0, MAX]; returns string for TextInput value.
 */
export function stepPickScoreString(current: string, delta: number): string {
  const next = scoreStringToSteppableInt(current) + delta;
  const clamped = Math.min(MAX_COMPETITION_PREDICTED_SCORE, Math.max(0, next));
  return String(clamped);
}
